import React from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import Gioia from "pictures/gioia-igretec.jpeg";
import Femme from "pictures/square-femme.jpg";

const RecruiterTestimonial = () => {
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const testimonials = [
    {
      key: 1,
      textId: "landing_recruiter.testimonial.gioia_text",
      nameId: "landing_recruiter.testimonial.gioia_name",
      positionId: "landing_recruiter.testimonial.gioia_position",
      companyUrl: `https://squarehub.eu/${i18nLocale}/candidate/company/132?redirectTo=recruiters`,
      photo: Gioia,
      initials: "GP",
      alt: "Gioia Parrinello",
    },
    {
      key: 2,
      textId: "landing_recruiter.testimonial.isabel_text",
      nameId: "landing_recruiter.testimonial.isabel_name",
      positionId: "landing_recruiter.testimonial.isabel_position",
      companyUrl: `https://squarehub.eu/${i18nLocale}/candidate/company/118?redirectTo=recruiters`,
      photo: Femme,
      initials: "IV",
      alt: "Isabel Vanlerberghe",
    },
    {
      key: 3,
      textId: "landing_recruiter.testimonial.marilyn_text",
      nameId: "landing_recruiter.testimonial.marilyn_name",
      positionId: "landing_recruiter.testimonial.marilyn_position",
      companyUrl: `https://squarehub.eu/${i18nLocale}/candidate/company/481?redirectTo=recruiters`,
      photo: Femme,
      initials: "MM",
      alt: "Marilyn M.",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "32px", md: "48px" },
        px: { xs: "16px", sm: "24px", lg: "32px" },
        bgcolor: "hsl(220, 14%, 96%)",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: "32px",
        }}
      >
        {testimonials.map((t, i) => (
          <Box
            key={t.key}
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15 }}
            sx={{
              bgcolor: "hsl(0, 0%, 100%)",
              border: "1px solid hsl(220, 13%, 91%)",
              borderRadius: "1rem",
              p: { xs: "32px", md: "40px" },
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* Quote icon */}
            <Box
              sx={{
                position: "absolute",
                top: "24px",
                left: "24px",
                color: "hsla(145, 63%, 42%, 0.2)",
              }}
            >
              <Quote size={40} />
            </Box>

            {/* Quote text */}
            <Box
              component="blockquote"
              sx={{
                color: "hsl(220, 25%, 12%)",
                fontSize: "1rem",
                lineHeight: 1.75,
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                position: "relative",
                zIndex: 1,
                m: 0,
                p: 0,
                "& strong, & b": {
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                },
              }}
            >
              <FormattedMessage
                id={t.textId}
                values={{
                  b: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </Box>

            {/* Author */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "16px", mt: "24px" }}>
              {t.photo ? (
                <Box
                  component="img"
                  src={t.photo}
                  alt={t.alt}
                  sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    bgcolor: "hsla(145, 63%, 42%, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.875rem",
                      fontFamily: "Segoe UI Black, system-ui, sans-serif",
                      color: "hsl(145, 63%, 42%)",
                    }}
                  >
                    {t.initials}
                  </Box>
                </Box>
              )}
              <Box>
                <Box
                  component="p"
                  sx={{
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    color: "hsl(220, 25%, 12%)",
                    fontSize: "0.875rem",
                    m: 0,
                  }}
                >
                  <FormattedMessage id={t.nameId} />
                </Box>
                <Box
                  component="p"
                  sx={{
                    fontSize: "0.75rem",
                    color: "hsl(220, 10%, 46%)",
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    m: 0,
                    "& a": {
                      color: "hsl(145, 63%, 42%)",
                      textDecoration: "underline",
                    },
                  }}
                >
                  <FormattedMessage
                    id={t.positionId}
                    values={{
                      a: (chunks) => (
                        <a
                          href={t.companyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RecruiterTestimonial;
